import { useState } from 'react';
import { Sparkles, Pencil, Loader2, X, Check, RotateCcw } from 'lucide-react';
import { useConfirmation } from './shared/ZCard';
import api from '../lib/api';

// Rewrite only. "Ask Zephyr" and "Research" were retired with the in-app chat
// (Phase 5): questions about an entry go to Claude over MCP.
export default function AIActionSheet({ entry, sourceApp, onClose, onAcceptRewrite }) {
  const feedback = useConfirmation();
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null); // { title, body, urgency, entry_type, domain }

  const entryBody = entry.body || entry.notes || entry.caption || '';
  const entryTitle = entry.title || 'Untitled';

  // Generate rewrite and show preview — don't save yet
  const handleRewrite = async (mode = 'default') => {
    if (loading) return;
    setLoading(true);
    try {
      const fullText = [entryTitle, entryBody].filter(Boolean).join('\n');
      const { data } = await api.post('/tasks/rewrite', { text: fullText, mode });
      setPreview({
        title: data.title || entryTitle,
        body: data.body || '',
        urgency: data.urgency,
        entry_type: data.entry_type,
        domain: data.domain,
      });
    } catch {
      feedback.fail('Rewrite failed');
    } finally {
      setLoading(false);
    }
  };

  // User accepts the preview — save via parent callback
  const handleAccept = async () => {
    if (!preview || loading) return;
    setLoading(true);
    try {
      await onAcceptRewrite(preview);
      feedback.confirm('Entry updated');
      onClose();
    } catch {
      feedback.fail('Update failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}
      onClick={onClose}
    >
      <div
        className="w-full md:max-w-[480px] overflow-hidden rounded-t-[20px] md:rounded-2xl"
        style={{ background: '#111118', border: '0.5px solid rgba(255,255,255,0.08)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 flex items-start justify-between" style={{ borderBottom: '0.5px solid rgba(255,255,255,0.06)' }}>
          <div>
            <div className="flex items-center gap-2">
              <Sparkles size={14} style={{ color: '#6B35B8' }} />
              <span className="text-sm font-medium" style={{ color: '#f0f0f5' }}>
                {preview ? 'Preview rewrite' : 'AI Actions'}
              </span>
            </div>
            <p className="text-xs truncate mt-0.5" style={{ color: '#b0b4c0', maxWidth: 260 }}>{entryTitle}</p>
          </div>
          <button onClick={onClose} className="p-1 transition-colors" style={{ color: '#b0b4c0' }}>
            <X size={16} />
          </button>
        </div>

        {preview ? (
          /* ── Preview panel ── */
          <>
            {/* New title */}
            <div className="px-4 pt-4 pb-3" style={{ borderBottom: '0.5px solid rgba(255,255,255,0.06)' }}>
              <p className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color: '#6B35B8' }}>Title</p>
              <p className="text-sm font-medium" style={{ color: '#f0f0f5' }}>{preview.title}</p>
            </div>

            {/* New body */}
            <div className="px-4 pt-3 pb-4" style={{ borderBottom: '0.5px solid rgba(255,255,255,0.06)', maxHeight: 260, overflowY: 'auto' }}>
              <p className="text-[10px] font-mono uppercase tracking-widest mb-1.5" style={{ color: '#6B35B8' }}>Body</p>
              <p className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: preview.body ? '#C8C8C8' : '#555' }}>
                {preview.body || '(no body)'}
              </p>
            </div>

            {/* Accept / Discard */}
            <div className="px-4 py-3 flex gap-2">
              <button
                onClick={() => setPreview(null)}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 text-sm font-medium disabled:opacity-50 transition-colors"
                style={{ background: 'rgba(255,255,255,0.06)', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 999, padding: '10px 0', color: '#b0b4c0' }}
              >
                <RotateCcw size={14} /> Discard
              </button>
              <button
                onClick={handleAccept}
                disabled={loading}
                className="flex-1 flex items-center justify-center gap-2 text-sm font-medium text-white disabled:opacity-50 transition-colors"
                style={{ background: '#6B35B8', borderRadius: 999, padding: '10px 0' }}
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
                Apply
              </button>
            </div>
          </>
        ) : (
          /* ── Action list ── */
          <>
            {/* Rewrite */}
            <div
              onClick={() => handleRewrite()}
              className="flex items-center gap-3 px-4 py-4 cursor-pointer transition-colors hover:bg-white/[0.05]"
              style={{ opacity: loading ? 0.4 : 1, pointerEvents: loading ? 'none' : undefined }}
            >
              <div className="shrink-0">
                {loading
                  ? <Loader2 size={18} className="animate-spin" style={{ color: '#b0b4c0' }} />
                  : <Pencil size={18} style={{ color: '#b0b4c0' }} />
                }
              </div>
              <div>
                <p className="text-sm font-medium" style={{ color: '#f0f0f5' }}>Rewrite</p>
                <p className="text-xs" style={{ color: '#b0b4c0' }}>
                  {sourceApp !== 'giraffe' ? 'Condense, sharpen and classify — preview before saving' : 'Condense and sharpen — preview before saving'}
                </p>
              </div>
            </div>

          </>
        )}
      </div>
    </div>
  );
}
